/**
 * Custom hook for fetching version data using TanStack Query
 * Replaces useLinkedTableVersions with better caching and parallel fetching
 */

import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import type { VersionData } from '@/features/linkedTables/editLinkedTable/utils/viewHelpers';
import { useRpc } from '@/lib/rpc';
import { logger } from '@/lib/logger';
import { queryKeys } from '@/lib/queryKeys';
import type { LinkedTable, LinkedTableRelationship } from '@/lib/types';

interface UseLinkedTableVersionsQueryProps {
  linkedTable: LinkedTable | null;
  relationships: LinkedTableRelationship[];
  isCreateMode: boolean;
}

interface UseLinkedTableVersionsQueryResult {
  baseVersions: VersionData[];
  relationshipVersionsMap: Record<string, VersionData[]>;
  isLoading: boolean;
}

/**
 * Hook for fetching versions for version display using TanStack Query
 * Fetches versions in parallel for better performance
 */
export const useLinkedTableVersionsQuery = ({
  linkedTable,
  relationships,
  isCreateMode,
}: UseLinkedTableVersionsQueryProps): UseLinkedTableVersionsQueryResult => {
  const rpc = useRpc();

  // Create stable IDs for query tracking
  // Use SQL-native schema: extract base table ID from tables[0]
  const tableId = linkedTable?.tables?.[0]?.target_id;
  const relationshipIds = useMemo(
    () => relationships.map((r) => `${r.target_id}:${r.alias}`).join(','),
    [relationships]
  );

  // Build query array for all versions we need to fetch
  const queries = useMemo(() => {
    const queryArray: Array<{
      queryKey: readonly (string | undefined)[];
      queryFn: () => Promise<VersionData[]>;
      enabled: boolean;
    }> = [];

    // Base table versions query
    if (tableId) {
      queryArray.push({
        queryKey: queryKeys.tables.versions(tableId),
        queryFn: async () => {
          logger.debug('Fetching base table versions', {
            tableId,
          });
          const res = await rpc.api.private.versions.$get({
            query: { table_id: tableId },
          });
          if (!res.ok) return [];
          const response = await res.json();
          const versionsArray = response.data || [];
          return (versionsArray as unknown[]).filter(
            (u: unknown) =>
              typeof u === 'object' &&
              u !== null &&
              'id' in u &&
              'file_name' in u &&
              'status' in u &&
              (u as { status: string }).status === 'completed'
          ) as VersionData[];
        },
        enabled: !isCreateMode && !!tableId,
      });
    }

    // Relationship versions queries
    relationships.forEach((rel) => {
      if (rel.target_id) {
        queryArray.push({
          queryKey: queryKeys.tables.versions(rel.target_id),
          queryFn: async () => {
            logger.debug('Fetching relationship versions', {
              alias: rel.alias,
              target_id: rel.target_id,
            });
            try {
              const res = await rpc.api.private.versions.$get({
                query: { table_id: rel.target_id },
              });
              if (!res.ok) return [];
              const response = await res.json();
              const versionsArray = response.data || [];
              return (versionsArray as unknown[]).filter(
                (u: unknown) =>
                  typeof u === 'object' &&
                  u !== null &&
                  'id' in u &&
                  'file_name' in u &&
                  'status' in u &&
                  (u as { status: string }).status === 'completed'
              ) as VersionData[];
            } catch (error) {
              logger.error('Failed to fetch versions for relationship', error, {
                relationshipAlias: rel.alias,
                target_id: rel.target_id,
              });
              return [];
            }
          },
          enabled: !isCreateMode && !!rel.target_id,
        });
      }
    });

    return queryArray;
    // Use stable string for relationships instead of the array itself
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId, relationshipIds, isCreateMode]);

  // Execute all queries in parallel
  const results = useQueries({
    queries: queries.map((q) => ({
      ...q,
      staleTime: 2 * 60 * 1000, // 2 minutes - versions don't change often
      gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache longer
    })),
  });

  // Extract base versions (first query)
  const baseVersions = useMemo(() => {
    if (queries.length === 0 || !tableId) return [];
    const baseResult = results[0];
    return (baseResult.data || []) as VersionData[];
  }, [results, queries.length, tableId]);

  // Extract relationship versions (remaining queries)
  const relationshipVersionsMap = useMemo(() => {
    const map: Record<string, VersionData[]> = {};
    if (queries.length <= 1) return map;

    // Skip first query (base), process relationship queries
    relationships.forEach((rel, index) => {
      if (rel.target_id && rel.alias) {
        const resultIndex = index + 1; // +1 because first is base
        if (resultIndex < results.length) {
          const result = results[resultIndex];
          map[rel.alias] = (result.data || []) as VersionData[];
        }
      }
    });

    return map;
  }, [results, relationships, queries.length]);

  const isLoading = results.some((result) => result.isLoading);

  return {
    baseVersions,
    relationshipVersionsMap,
    isLoading,
  };
};

